/* Training Client barrel export */

import { ITrainingClient } from './ITrainingClient';
import { TrainingClientApi } from './trainingClientAPI';
import {
  CreateTrainingCategoryDto,
  UpdateTrainingCategoryDto,
  CreateTrainingModuleDto,
  UpdateTrainingModuleDto,
  SubmitQuizDto,
} from './trainingTypes';

// Re-export types
export * from './trainingTypes';
export * from './ITrainingClient';

/* ---- Singleton Instance ---- */

/**
 * Training client singleton instance.
 * Uses real API client.
 * Import directly: `import { trainingClient } from 'app/services/api/compliance/training';`
 */
export const trainingClient: ITrainingClient = new TrainingClientApi();

/* ---- Factory Function (Backwards Compatibility) ---- */

/**
 * @deprecated Use `trainingClient` directly instead.
 * Get the TrainingClient instance.
 */
export function getTrainingClient(): ITrainingClient {
  return trainingClient;
}

/* ---- Backwards Compatibility ---- */

/**
 * @deprecated Use getTrainingClient() instead
 */
export class TrainingClient implements ITrainingClient {
  private client: ITrainingClient;

  constructor() {
    this.client = getTrainingClient();
  }

  getCategories = (opts?: Parameters<ITrainingClient['getCategories']>[0]) => this.client.getCategories(opts);
  getCategory = (id: number) => this.client.getCategory(id);
  createCategory = (dto: CreateTrainingCategoryDto) => this.client.createCategory(dto);
  updateCategory = (id: number, dto: UpdateTrainingCategoryDto) => this.client.updateCategory(id, dto);
  deleteCategory = (id: number) => this.client.deleteCategory(id);
  
  getModules = (filters?: Parameters<ITrainingClient['getModules']>[0]) => this.client.getModules(filters);
  getModule = (id: number) => this.client.getModule(id);
  createModule = (dto: CreateTrainingModuleDto) => this.client.createModule(dto);
  updateModule = (id: number, dto: UpdateTrainingModuleDto) => this.client.updateModule(id, dto);
  deleteModule = (id: number) => this.client.deleteModule(id);
  publishModule = (id: number) => this.client.publishModule(id);

  getSectionById = (moduleId: number, contentId: number) => this.client.getSectionById(moduleId, contentId);
  createSection = (moduleId: number, dto: Parameters<ITrainingClient['createSection']>[1]) => this.client.createSection(moduleId, dto);
  updateSection = (moduleId: number, contentId: number, dto: Parameters<ITrainingClient['updateSection']>[2]) => this.client.updateSection(moduleId, contentId, dto);
  deleteSection = (moduleId: number, contentId: number) => this.client.deleteSection(moduleId, contentId);

  createQuiz = (moduleId: number, dto: Parameters<ITrainingClient['createQuiz']>[1]) => this.client.createQuiz(moduleId, dto);
  updateQuiz = (moduleId: number, quizId: number, dto: Parameters<ITrainingClient['updateQuiz']>[2]) => this.client.updateQuiz(moduleId, quizId, dto);
  deleteQuiz = (moduleId: number, quizId: number) => this.client.deleteQuiz(moduleId, quizId);
  
  getCompletionsByUserId = (userId: number, franchiseId?: number) => this.client.getCompletionsByUserId(userId, franchiseId);
  getCompletionByAssignmentId = (assignmentId: number) => this.client.getCompletionByAssignmentId(assignmentId);
  startTraining = (dto: Parameters<ITrainingClient['startTraining']>[0]) => this.client.startTraining(dto);
  submitQuiz = (dto: SubmitQuizDto) => this.client.submitQuiz(dto);
  updateProgress = (assignmentId: number, currentStep: number) => this.client.updateProgress(assignmentId, currentStep);
  
  getAllAssignments = (filters: Parameters<ITrainingClient['getAllAssignments']>[0]) => this.client.getAllAssignments(filters);
  getAssignmentById = (assignmentId: number) => this.client.getAssignmentById(assignmentId);
  bulkAssign = (dto: Parameters<ITrainingClient['bulkAssign']>[0]) => this.client.bulkAssign(dto);
  deleteAssignment = (assignmentId: number) => this.client.deleteAssignment(assignmentId);
  getPersonasByStore = (storeNumber: string) => this.client.getPersonasByStore(storeNumber);
  getUsersByStore = (storeNumber: string) => this.client.getUsersByStore(storeNumber);
  getUsersByFranchise = (franchiseId: number) => this.client.getUsersByFranchise(franchiseId);
  getAccessibleFranchises = (franchiseId?: number) => this.client.getAccessibleFranchises(franchiseId);
  
  getMyCertificates = () => this.client.getMyCertificates();
  getAllCertificates = (filters?: Parameters<ITrainingClient['getAllCertificates']>[0]) => this.client.getAllCertificates(filters);
  getCertificateById = (id: number) => this.client.getCertificateById(id);
  downloadCertificate = (id: number) => this.client.downloadCertificate(id);
  verifyCertificate = (certificateNumber: string) => this.client.verifyCertificate(certificateNumber);
  issueCertificate = (completionId: number) => this.client.issueCertificate(completionId);
  revokeCertificate = (id: number, reason: string) => this.client.revokeCertificate(id, reason);
  
  generateSummaryReport = (filter: Parameters<ITrainingClient['generateSummaryReport']>[0]) => this.client.generateSummaryReport(filter);
  generateCompletionReport = (filter: Parameters<ITrainingClient['generateCompletionReport']>[0]) => this.client.generateCompletionReport(filter);
  previewSummaryReport = (filter: Parameters<ITrainingClient['previewSummaryReport']>[0]) => this.client.previewSummaryReport(filter);
  previewCompletionReport = (filter: Parameters<ITrainingClient['previewCompletionReport']>[0]) => this.client.previewCompletionReport(filter);
  exportSummaryReport = (filter: Parameters<ITrainingClient['exportSummaryReport']>[0]) => this.client.exportSummaryReport(filter);
  exportCompletionReport = (filter: Parameters<ITrainingClient['exportCompletionReport']>[0]) => this.client.exportCompletionReport(filter);
  
  getDashboardMetrics = (filter: Parameters<ITrainingClient['getDashboardMetrics']>[0]) => this.client.getDashboardMetrics(filter);
  getCompletionTrends = (months: number, filter: Parameters<ITrainingClient['getCompletionTrends']>[1]) => this.client.getCompletionTrends(months, filter);
  getStorePerformance = (filter: Parameters<ITrainingClient['getStorePerformance']>[0]) => this.client.getStorePerformance(filter);
  sendNotification = (assignmentId: number, franchiseId?: number, notificationMessage?: string) => this.client.sendNotification(assignmentId, franchiseId, notificationMessage);
  getStoresByFranchise = (franchiseId: number) => this.client.getStoresByFranchise(franchiseId);
}
